/**
 * notifications.ts — Desktop Push Notification Adapter (Renderer Side)
 *
 * Аналог mobile-app/src/capacitor/pushNotifications.ts для Desktop.
 *
 * Стратегия push-уведомлений на macOS Desktop:
 *
 * 1. Приложение ОТКРЫТО:
 *    - In-app Toast (существующий механизм React)
 *    - Нативное уведомление ТОЛЬКО если окно не в фокусе
 *
 * 2. Приложение СВЁРНУТО В ТРЕЙ:
 *    - Нативное macOS уведомление через IPC → notifications.ts (main)
 *    - Клик по уведомлению → window.show() + navigate()
 *
 * 3. Приложение ЗАКРЫТО:
 *    - Требуется FCM Web Push с service worker (реализован в React-приложении)
 *    - Или APNs (только Mac App Store build)
 *
 * Токен устройства для backend: регистрируется как platform='desktop-web'
 * с использованием существующего Firebase Web SDK в React-приложении.
 */

import { isDesktop } from './platform';
import { showNativeNotification, requestNotificationPermission, onDeepLink } from './ipc';

export interface DesktopPushCallbacks {
  onNotificationTapped?: (chatId: string) => void;
  onDeepLinkNavigate?: (path: string) => void;
}

let permissionGranted = false;

console.log('[Desktop Push] Module loaded. isDesktop =', isDesktop);

/**
 * Инициализация desktop-уведомлений.
 * Вызывать после аутентификации пользователя.
 */
export async function initDesktopNotifications(callbacks: DesktopPushCallbacks): Promise<void> {
  if (!isDesktop) return;

  const permission = await requestNotificationPermission();
  permissionGranted = permission === 'granted';

  if (!permissionGranted) {
    console.warn('[Desktop Push] Notification permission denied');
    return;
  }

  onDeepLink((url) => {
    try {
      const parsed = new URL(url);
      const host = parsed.hostname;
      const segments = parsed.pathname.replace(/^\//, '').split('/');

      if (host === 'chat' && segments[0]) {
        callbacks.onNotificationTapped?.(segments[0]);
      } else {
        callbacks.onDeepLinkNavigate?.(url);
      }
    } catch {
      callbacks.onDeepLinkNavigate?.(url);
    }
  });

  console.log('[Desktop Push] Initialized. Notifications:', permissionGranted ? 'granted' : 'denied');
}

/**
 * Показать уведомление о новом сообщении.
 * Вызывать когда приложение не в фокусе.
 */
export async function notifyNewMessage(options: {
  senderName: string;
  text: string;
  chatId: string;
  avatarUrl?: string;
}): Promise<void> {
  if (!isDesktop || !permissionGranted) return;

  await showNativeNotification({
    title: options.senderName,
    body: options.text.length > 100 ? options.text.substring(0, 100) + '…' : options.text,
    chatId: options.chatId,
    imageUrl: options.avatarUrl,
  });
}

/**
 * Показать уведомление о входящем звонке.
 */
export async function notifyIncomingCall(options: {
  callerName: string;
  isVideo: boolean;
  chatId: string;
}): Promise<void> {
  if (!isDesktop || !permissionGranted) return;

  await showNativeNotification({
    title: `Входящий ${options.isVideo ? 'видео' : 'аудио'}звонок`,
    body: `${options.callerName} звонит вам`,
    chatId: options.chatId,
  });
}
